import { isDefined, isNotDefined } from '@sgme/fp';
import { ISearchResponse, ISort, referentialApiConnector, tradesBlotterApiConnector } from 'apiConnector/blotters';
import { isPageVisible } from 'components/shared/Blotter/Grid/Hooks/utils';
import { isNavigatingInTradeBlotter, isNavigatingOutTradeBlotter } from 'epic/utils/navigation/navigation';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { ofType } from 'redux-observable';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  forkJoin,
  from,
  interval,
  map,
  mergeMap,
  Observable,
  of,
  SchedulerLike,
  skipUntil,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { merge, takeUntil } from 'rxjs/operators';
import { SpEpic } from 'shared/middlewares/epic/epicMiddleware.factory';
import { ActivityFilter, IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import {
  TradesBlottersItemsRequested,
  tradesBlottersColumnDefinitionReceived,
  tradesBlottersFetchColumnDefinitionFailed,
  tradesBlottersFilterChanged,
  tradesBlottersFilterEnabled,
  tradesBlottersItemsFetchingFailed,
  tradesBlottersItemsReceived,
  tradesBlottersItemsReloaded,
  tradesBlottersItemsRequested,
  tradesBlottersPlatformsFetched,
  tradesBlottersPlatformsFetchedFailed,
  tradesBlottersPlatformsFetching,
} from 'state/blotters/trades/tradesBlotters.actions';
import {
  getTradesBlottersAvailablePlatformsFetchingStatus,
  isTradesBlottersDatesIntervalInThePast,
  selectTradesBlotterRequestByBlotterType,
  selectTradesBlottersType,
} from 'state/blotters/trades/tradesBlotters.selector';
import { TradesBlottersItemByBlotterType } from 'state/blotters/trades/types/blotterItem';
import {
  type BlotterType,
  isClientSideBlotter,
  isPageFilterOrPreferenceBlotterType,
  PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES,
} from 'state/blotters/trades/types/types';
import { isInTradesBlotterRoute } from 'state/selectors';
import { EpicWithThunk } from 'state/state.common';
import { HttpError } from 'utils/http/httpClient.model';
import { dispatchAllThunk } from 'utils/redux/dispatchAllThunk';

export function fetchTradesBlottersTypeEpic(): SpEpic {
  return action$ =>
    action$.pipe(
      ofType('TRADES_BLOTTERS_FILTERS_LOADED'),
      map(({ blotter }) => {
        return tradesBlottersFilterEnabled(blotter);
      }),
    );
}

export interface FetchColumnDefinitionEpicApi {
  fetchColumnDefinition<T extends BlotterType>(
    blotterType: T,
  ): Observable<IBlotterColumnDefinition<TradesBlottersItemByBlotterType<T>>[]>;
}

export function fetchTradesBlottersColumnDefinitionEpic(): EpicWithThunk[] {
  return PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES.map(
    blotterType =>
      (actions$, _state$, { httpClient }) =>
        actions$.pipe(
          ofType('TRADES_BLOTTERS_FILTER_ENABLED'),
          filter(({ blotter }) => blotter === blotterType),
          take(1),
          mergeMap(({ blotter }) => {
            let blotters: BlotterType[] = [blotter];
            if (blotter === 'dlpProducts') {
              blotters = ['dlpProductTrades', 'dlpProducts'] satisfies BlotterType[];
            }
            return forkJoin(
              blotters.map(b =>
                tradesBlotterApiConnector(httpClient)
                  .fetchColumnDefinition(b)
                  .pipe(
                    map(colDef => tradesBlottersColumnDefinitionReceived(colDef, b)),
                    catchError((_: Error) => {
                      return of(tradesBlottersFetchColumnDefinitionFailed(b));
                    }),
                  ),
              ),
            ).pipe(map(dispatchAllThunk));
          }),
        ),
  );
}
export interface BlotterRequest {
  sort?: readonly ISort[];
  page?: number;
  size?: number;
  fullTextSearch?: string;
  activityScope: ActivityFilter;
  startDate: string;
  endDate?: string;
}
export interface TradesOrProductsBlotterRequest extends BlotterRequest {
  currency?: string;
  platforms?: string;
  product?: string;
  status?: string;
}

export type DlpBlotterRequest = {
  sort?: readonly ISort[];
} & DlpBlotterTopRequest;

export type DlpBlotterTopRequest = {
  page?: number;
  size?: number;
  fullTextSearch?: string;
  activityScope: ActivityFilter;
  startDate?: string;
  endDate?: string;
};

export type BlotterRequestByBlotterType<T extends BlotterType> = T extends 'trades' | 'products'
  ? TradesOrProductsBlotterRequest
  : DlpBlotterRequest;
export interface FetchItemsEpicApi {
  search<T extends BlotterType>(
    request: BlotterRequestByBlotterType<T>,
    blotter: BlotterType,
  ): Observable<ISearchResponse<TradesBlottersItemByBlotterType<T>>>;
}
function isTradesOrProductsBlotterRequest(_val: unknown, blotter: BlotterType): _val is TradesOrProductsBlotterRequest {
  return blotter === 'trades' || blotter === 'products';
}

function isValidInterval<T extends BlotterType>(request: BlotterRequestByBlotterType<T>, blotter: T): boolean {
  if (!isTradesOrProductsBlotterRequest(request, blotter)) {
    return true;
  }
  if (!isDefined(request.endDate) || !isDefined(request.startDate)) {
    return true;
  }
  return new Date(request.startDate) < new Date(request.endDate);
}

export const TRADE_BLOTTER_POLLING_FREQUENCY = 1500;

export function fetchDlpProductTradesBlotterEpic(): EpicWithThunk {
  return (actions$, _, { httpClient }) => {
    return actions$.pipe(
      ofType('TRADES_BLOTTERS_ITEMS_REQUESTED', 'TRADE_CANCELLED'),
      filter(
        action =>
          (action.type === 'TRADES_BLOTTERS_ITEMS_REQUESTED' && action.blotter === 'dlpProductTrades') ||
          action.type === 'TRADE_CANCELLED',
      ),
      mergeMap(action => {
        const isin =
          action.type === 'TRADES_BLOTTERS_ITEMS_REQUESTED'
            ? (action as TradesBlottersItemsRequested<'dlpProductTrades'>).request
            : action.productId;
        if (isNotDefined(isin)) {
          return EMPTY;
        }
        return tradesBlotterApiConnector(httpClient)
          .searchDlpProductTrades(isin)
          .pipe(
            map(({ entries }) => tradesBlottersItemsReceived('dlpProductTrades', entries, isin)),
            catchError((error: HttpError<{ rejections: NextWorkflowError[] }>) =>
              of(tradesBlottersItemsFetchingFailed('dlpProductTrades', isin, error.payload?.rejections ?? [])),
            ),
          );
      }),
    );
  };
}

export function fetchTradesBlottersItemsEpic(
  pollingFrequency: number = TRADE_BLOTTER_POLLING_FREQUENCY,
  scheduler?: SchedulerLike,
): EpicWithThunk[] {
  return PAGE_FILTER_OR_PREFERENCE_BLOTTER_TYPES.map(blotterType => {
    return (action$, state$, { httpClient }) => {
      const navigateIn$ = action$.pipe(filter(isNavigatingInTradeBlotter));
      const filtersLoaded$ = action$.pipe(ofType('TRADES_BLOTTERS_FILTERS_LOADED'));
      const filterChanged$ = action$.pipe(
        ofType('TRADES_BLOTTERS_FILTER_CHANGED'),
        filter(a => a.blotter === blotterType),
        debounceTime(pollingFrequency, scheduler),
      );
      const tradeBlotterActions$ = action$.pipe(
        ofType(
          'TRADES_BLOTTERS_FILTERS_LOADED',
          'TRADES_BLOTTERS_ITEMS_PER_PAGE_CHANGED',
          'TRADES_BLOTTERS_ITEMS_RELOADED',
          'TRADES_BLOTTERS_SORT_CHANGED',
          'TRADES_BLOTTERS_FILTER_ENABLED',
          'TRADES_BLOTTERS_ADVANCED_FILTERS_TOGGLED',
          'TRADES_BLOTTERS_COLUMN_DEFINITION_RECEIVED',
          'TRADES_BLOTTERS_MANUAL_REFRESH_REQUESTED',
        ),
        filter(a => a.blotter === blotterType),
      );

      return tradeBlotterActions$.pipe(
        merge(filterChanged$, navigateIn$),
        skipUntil(action$.pipe(filter(({ type }) => type === 'TRADES_BLOTTERS_FILTERS_LOADED'))),
        merge(filtersLoaded$),
        withLatestFrom(state$),
        filter(([_, state]) => {
          const blotter = selectTradesBlottersType(state);
          return isClientSideBlotter(blotter);
        }),
        map(([_, state]) => {
          const blotter = selectTradesBlottersType(state);
          const request = selectTradesBlotterRequestByBlotterType(state, blotter);
          return { blotter, request };
        }),
        switchMap(({ blotter, request }) => {
          if (!isValidInterval(request, blotter)) {
            return of(tradesBlottersItemsFetchingFailed(blotter, undefined, undefined));
          }
          return tradesBlotterApiConnector(httpClient)
            .search(request, blotter)
            .pipe(
              map(({ entries }) => {
                return tradesBlottersItemsReceived(
                  blotter,
                  entries as TradesBlottersItemByBlotterType<typeof blotter>[],
                  undefined,
                );
              }),
              startWith(tradesBlottersItemsRequested(blotter, undefined)),
              catchError((_: Error) => {
                return of(tradesBlottersItemsFetchingFailed(blotter, undefined, undefined));
              }),
            );
        }),
      );
    };
  });
}

export function pollTradesBlottersItemsEpic(
  pollingFrequency: number = 10_000,
  scheduler?: SchedulerLike,
): EpicWithThunk {
  return (actions$, state$) => {
    const navigationOut$: Observable<any> = actions$.pipe(filter(isNavigatingOutTradeBlotter));

    return actions$.pipe(
      ofType('TRADES_BLOTTERS_ITEMS_RECEIVED'),
      withLatestFrom(state$),
      filter(([_, state]) => {
        const blotter = selectTradesBlottersType(state);
        return blotter !== 'trades' && blotter !== 'products';
      }),
      switchMap(([{ blotter }, state]) =>
        interval(pollingFrequency, scheduler).pipe(
          filter(() => {
            if (isPageFilterOrPreferenceBlotterType(blotter)) {
              const isEndDateFilterInThePast = isTradesBlottersDatesIntervalInThePast(state, blotter);
              return isInTradesBlotterRoute(state) && isPageVisible() && !isEndDateFilterInThePast;
            }
            return false;
          }),
          map(() => blotter),
          takeUntil(navigationOut$),
        ),
      ),
      filter(b => b !== 'dlpProducts'),
      map(blotter => {
        return tradesBlottersItemsReloaded(blotter);
      }),
    );
  };
}

export function waitUntilBlotterLoadedToSetDefaultFilter(): EpicWithThunk {
  return (actions$, _) => {
    const globalFilterLoaded$ = actions$.pipe(ofType('TRADES_BLOTTERS_GLOBAL_FILTERS_LOADED'), take(1));
    const filterLoaded$ = actions$.pipe(ofType('TRADES_BLOTTERS_FILTERS_LOADED'), take(1));
    const filterSelected$ = actions$.pipe(ofType('TRADES_BLOTTERS_FILTER_ENABLED'), take(1));
    const columnDefinitionLoaded$ = actions$.pipe(ofType('TRADES_BLOTTERS_COLUMN_DEFINITION_RECEIVED'), take(1));
    const columnPreferenceLoaded$ = actions$.pipe(ofType('TRADES_BLOTTERS_PREFERENCE_STATE_LOADED'), take(1));

    const result$ = combineLatest([
      actions$.pipe(ofType('TRADES_BLOTTERS_OPEN_DLP_PRODUCT')),
      columnDefinitionLoaded$,
      columnPreferenceLoaded$,
      globalFilterLoaded$,
      filterLoaded$,
      filterSelected$,
    ]).pipe(
      mergeMap(([action]) => {
        return from([
          tradesBlottersFilterChanged('dlpProducts', 'quickSearchTerm', action.isin),
          tradesBlottersFilterEnabled('dlpProducts'),
        ]);
      }),
    );
    return result$;
  };
}

export function fetchTradesBlotterPlatformsEpic(): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    const { getTradesBlottersAvailablePlatforms: getTradesBlotterAvailablePlatforms } =
      referentialApiConnector(httpClient);

    return action$.pipe(
      filter(action => isNavigatingInTradeBlotter(action)),
      withLatestFrom(state$),
      filter(([, state]) => {
        const status = getTradesBlottersAvailablePlatformsFetchingStatus(state);
        return status !== 'success';
      }),
      switchMap(() =>
        getTradesBlotterAvailablePlatforms().pipe(
          map((payload, _index) => tradesBlottersPlatformsFetched(payload)),
          startWith(tradesBlottersPlatformsFetching()),
          catchError(err => of(tradesBlottersPlatformsFetchedFailed(err))),
        ),
      ),
    );
  };
}
